import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, ChevronRight } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import ContactForm from "../components/ContactForm";
import { SITE, SERVICES } from "../lib/site";
import { PHOTOS } from "../lib/photos";

// Mapping slug de service → catégories de photos pertinentes (ordre = priorité).
const SERVICE_PHOTO_CATS = {
  "traitement-termites-bordeaux": ["Cordonnets", "Bois infesté", "Essaimage", "Pièges-appâts"],
  "traitement-bois-charpente": ["Charpente", "Bois infesté", "Chantier"],
  "traitement-preventif": ["Pièges-appâts", "Chantier", "Charpente"],
  "traitement-merule": ["Cave & humidité", "Bois infesté"],
  "traitement-humidite": ["Cave & humidité"],
};

function getServicePhotos(slug, max = 6) {
  const cats = SERVICE_PHOTO_CATS[slug] || [];
  const out = [];
  cats.forEach((c) => {
    PHOTOS.filter((p) => p.cat === c).forEach((p) => {
      if (out.length < max && !out.find((x) => x.src === p.src)) out.push(p);
    });
  });
  return out;
}

function PageHeader({ eyebrow, title, intro }) {
  return (
    <section className="bg-[#E8DCC9]/40 border-b border-[#D5CABD]">
      <div className="container-x py-16 lg:py-24">
        <div className="text-xs text-gray-500 mb-6 flex items-center gap-1">
          <Link to="/" className="hover:text-gray-800">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-800">{eyebrow}</span>
        </div>
        <div className="eyebrow mb-3">{eyebrow}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-gray-900 leading-[1.05] mb-5">
          {title}
        </h1>
        {intro && <p className="text-lg text-gray-700 max-w-2xl">{intro}</p>}
      </div>
    </section>
  );
}

export function ServicesList() {
  return (
    <div data-testid="services-page">
      <SeoHead
        title="Services traitement termites & bois Bordeaux Gironde | Termite Habitat 33"
        description="Traitement termites Bordeaux, charpente, expertise obligatoire Gironde, traitement préventif, mérule, humidité. 6 protocoles d'expert. Garantie 5 ans."
        canonical={SITE.url + "/services"}
        keywords={["services termites Bordeaux", "traitement bois Gironde", "expertise termites"]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Services", url: SITE.url + "/services" },
        ]}
      />
      <PageHeader
        eyebrow="Services"
        title="Traitement termites, bois, mérule et humidité en Gironde."
        intro="Six protocoles spécialisés pour traiter, prévenir et certifier l'expertise parasitaire de votre bien à Bordeaux et en Gironde."
      />
      <section className="container-x py-20 grid md:grid-cols-2 gap-6">
        {SERVICES.map((s) => (
          <Link
            key={s.slug}
            to={`/services/${s.slug}`}
            className="group p-8 bg-white border border-[#D5CABD] hover:border-[#7A2E39] hover:shadow-md transition-all rounded-sm"
            data-testid={`service-link-${s.slug}`}
          >
            <h2 className="font-serif text-2xl text-gray-900 mb-2">{s.title}</h2>
            <p className="text-gray-600 mb-5">{s.short}</p>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A2E39] inline-flex items-center gap-1">
              Détail du protocole <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </section>
      {/* Video showcase services */}
      <section className="container-x py-12">
        <div className="grid md:grid-cols-2 gap-6 items-center bg-white border border-[#D5CABD] rounded-sm overflow-hidden">
          <video
            src="https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/p4ig16hd_20250303_204822.mp4"
            muted
            autoPlay
            loop
            playsInline
            preload="metadata"
            className="w-full aspect-video object-cover bg-black"
            aria-label="Bois contaminé par les termites — chantier en Gironde"
            data-testid="services-video"
          />
          <div className="p-8">
            <div className="eyebrow mb-2">Démonstration terrain</div>
            <h2 className="font-serif text-3xl text-gray-900 mb-3">
              Du bois infesté à la structure assainie.
            </h2>
            <p className="text-gray-700 mb-4">
              Chaque chantier démarre par une expertise visuelle et un repérage des bois colonisés.
              S'ensuit le retrait des matières altérées, puis l'application du protocole de
              traitement adapté — le tout garanti 5 ans.
            </p>
            <Link to="/videos" className="link-underline text-sm font-bold uppercase tracking-widest" data-testid="services-videos-link">
              Voir toutes nos vidéos chantiers →
            </Link>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);
  const title = `${service.title} à Bordeaux & Gironde | Termite Bordeaux`;
  const description =
    `${service.short} Intervention en Bordeaux Métropole, Gironde et Bassin d'Arcachon. ` +
    "Garantie 5 ans, devis 48 h. Termite Habitat 33.";

  return (
    <div data-testid={`service-detail-${slug}`}>
      <SeoHead
        title={title}
        description={description}
        canonical={`${SITE.url}/services/${slug}`}
        keywords={service.keywords}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Services", url: SITE.url + "/services" },
          { name: service.title, url: `${SITE.url}/services/${slug}` },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: service.title,
          provider: {
            "@type": "LocalBusiness",
            name: SITE.legalName,
            telephone: SITE.phoneRaw,
            url: SITE.parentSite,
            sameAs: [SITE.parentSite, SITE.facebook],
          },
          areaServed: { "@type": "AdministrativeArea", name: "Gironde, France" },
          name: service.seoTitle || service.title,
          description: service.short,
          offers: { "@type": "Offer", availability: "https://schema.org/InStock" },
        }}
      />
      <PageHeader eyebrow="Service" title={service.title} intro={service.short} />
      <section className="container-x py-16 grid lg:grid-cols-3 gap-12">
        <article className="lg:col-span-2 prose-article">
          {service.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}

          <h2>Pourquoi choisir notre cabinet pour ce protocole ?</h2>
          <ul>
            <li>Expertise préalable détaillée et chiffrée.</li>
            <li>Produits certifiés CTB-P+, homologués Anses.</li>
            <li>Procès‑verbal de traitement transmis sous 5 jours.</li>
            <li>Garantie 5 ans : retraitement gratuit en cas de nouvelle infestation.</li>
            <li>Couverture complète Gironde : Bordeaux, Mérignac, Pessac, Talence, Bègles, Le Bouscat, Bruges, Lormont, Floirac, Arcachon, Libourne…</li>
          </ul>

          <h2>Comment se déroule une intervention ?</h2>
          <p>
            Premier contact téléphonique au {SITE.phone} ou via le formulaire ci‑contre. Nous fixons
            un rendez‑vous d'expertise gratuite dans les 24 à 48 h. Un devis transparent est remis sur place
            ou par e‑mail. L'intervention démarre sous 7 jours en moyenne.
          </p>

          <h2>Voir aussi</h2>
          <p>
            Cette prestation complète parfaitement nos autres protocoles. Pour aller plus loin,
            consultez les pages dédiées aux autres traitements :
          </p>
          <ul>
            {others.map((o) => (
              <li key={o.slug}>
                <Link to={`/services/${o.slug}`}>{o.title}</Link> — {o.short}
              </li>
            ))}
          </ul>

          {(() => {
            const svcPhotos = getServicePhotos(slug, 6);
            if (svcPhotos.length === 0) return null;
            return (
              <>
                <h2>Photos terrain — {service.title}</h2>
                <p>
                  Photos prises lors de chantiers réels en Gironde et illustrant ce protocole.
                  Retrouvez la photothèque complète sur la page <Link to="/photos">Photos</Link>.
                </p>
                <div className="not-prose grid sm:grid-cols-2 lg:grid-cols-3 gap-4 my-6" data-testid="service-photos">
                  {svcPhotos.map((p) => (
                    <figure key={p.src} className="border border-[#D5CABD] bg-white rounded-sm overflow-hidden">
                      <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                        <img src={p.src} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <figcaption className="p-3 text-xs text-gray-700">
                        <span className="block text-[10px] uppercase tracking-widest text-[#7A2E39] mb-1">{p.cat}</span>
                        {p.title}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </>
            );
          })()}

          <p>
            Vous pouvez aussi obtenir une <Link to="/devis">estimation gratuite en ligne</Link> en 6 questions :
            un devis détaillé vous est envoyé sous 24 h.
          </p>
        </article>

        <aside className="lg:sticky lg:top-28 self-start" data-testid="service-sidebar">
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm shadow-sm">
            <div className="eyebrow mb-2">Expertise gratuite</div>
            <h3 className="font-serif text-2xl text-gray-900 mb-4">Demandez un devis</h3>
            <a href={`tel:${SITE.phoneRaw}`} className="btn-primary w-full mb-3" data-testid="service-sidebar-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link to="/contact" className="btn-ghost w-full">Formulaire d'expertise</Link>
          </div>
          <div className="bg-[#E8DCC9]/40 border border-[#D5CABD] p-6 rounded-sm mt-5 text-sm text-gray-700">
            <div className="eyebrow mb-2">Mots-clés</div>
            <div className="flex flex-wrap gap-2">
              {service.keywords.map((k) => (
                <span key={k} className="bg-white border border-[#D5CABD] px-2 py-1 rounded-sm text-xs">{k}</span>
              ))}
            </div>
          </div>
        </aside>
      </section>
      <CTABand />
      <section className="container-x py-16">
        <h2 className="font-serif text-3xl text-gray-900 mb-6">Décrivez votre situation</h2>
        <div className="max-w-3xl">
          <ContactForm defaultSubject={`Demande – ${service.title}`} />
        </div>
      </section>
    </div>
  );
}

export { PageHeader };
